import React, { useCallback, useState } from 'react';

import { Logo } from '@/components/shared/Logo';
import { AsaphSelect } from '@/components/shared/Select/AsaphSelect';
import {
	SupportPricingTier1,
	SupportPricingTier2,
	SupportPricingTier3,
} from '@/config/constants';
import { useAuth } from '@/providers/AuthProvider';
import {
	openCheckout,
	useSubscription,
} from '@/providers/SubscriptionProvider';
import { useTeam } from '@/providers/TeamProvider';
import { Capacitor } from '@capacitor/core';

import { AccountDisplayProps } from '../../AccountPanel';
import { PlanCard } from '../Upgrade/PlanCard';
import styles from './SupportUs.module.scss';

const isCapacitor = Capacitor.isNativePlatform();

export const SupportUs: React.FC<
	Pick<AccountDisplayProps, 'popDisplayState'> & {
		onClose: () => void;
	}
> = ({ onClose, popDisplayState }) => {
	const { user } = useAuth();
	const { team } = useTeam();
	const { chargebeeInstance, cart } = useSubscription();
	const [currency, setCurrency] = useState<string>('USD');

	const currencyOptions = [
		{ value: 'AUD', label: `🇦🇺 AUD` },
		{ value: 'USD', label: `🇺🇸 USD` },
	];

	const handleOpenCheckout = useCallback(
		(planPriceIdPrefix: string) => {
			openCheckout({
				cbInstance: chargebeeInstance,
				cart,
				user,
				team,
				planPriceIdPrefix,
				period: 'Yearly',
				currency,
				onClose,
				popDisplayState,
			});
		},
		[
			chargebeeInstance,
			cart,
			user,
			team,
			currency,
			onClose,
			popDisplayState,
		],
	);

	return (
		<div className={styles['upgrade']}>
			<div className={styles['upgrade__logo']}>
				<Logo centered />
			</div>
			<div className="vsb-10" />
			<div className={styles['upgrade__content-text']}>
				Become an active supporter today and get a lifetime discount on
				future paid plans.
			</div>
			<div className="vsb-30" />
			{/* Mobile: hide upgrade methods */}
			{!isCapacitor && (
				<div>
					<div className={styles['upgrade__switches']}>
						<AsaphSelect
							value={currency}
							setValue={setCurrency}
							options={currencyOptions}
							className={styles['upgrade__select']}
						/>
					</div>
					<div className="vsb-30" />
					<PlanCard
						title="Support"
						price={`$${SupportPricingTier1.YEARLY} ${currency}/year`}
						lowerText={[
							'Support us to create more tools and training',
						]}
						buttonLabel="Support us"
						noIcon
						onClick={() =>
							handleOpenCheckout(
								SupportPricingTier1.PRICE_PLAN_ID_PREFIX,
							)
						}
					/>
					<div className="vsb-20" />

					<PlanCard
						title="Grow"
						price={`$${SupportPricingTier2.YEARLY} ${currency}/year`}
						lowerText={[
							'Support us and get direct input into our roadmap',
						]}
						buttonLabel="Help us grow"
						noIcon
						onClick={() =>
							handleOpenCheckout(
								SupportPricingTier2.PRICE_PLAN_ID_PREFIX,
							)
						}
					/>
					<div className="vsb-20" />

					<PlanCard
						title="Scale"
						price={`$${SupportPricingTier3.YEARLY} ${currency}/year`}
						lowerText={[
							'Support us and get Asaph optimized for your team',
						]}
						buttonLabel="Help us scale"
						noIcon
						onClick={() =>
							handleOpenCheckout(
								SupportPricingTier3.PRICE_PLAN_ID_PREFIX,
							)
						}
					/>
				</div>
			)}
		</div>
	);
};
